/**
 * PATH: packages/tenant-shared/src/settings/useShortcutOverrides.ts
 * FILE_ID: TENANT-SHARED-SHORTCUTS-OVERRIDES-001
 * PURPOSE: Load + persist the operator's per-user keyboard-shortcut
 *          customizations (remap / enable-disable) via /api/settings/ui/'s
 *          `shortcut_overrides` slice, with a localStorage fallback so the
 *          editor works offline / before a backend round-trip.
 * WHY: Keeps @quotech/ui's ShortcutCustomizer presentational (props only) while
 *      giving cross-hub/device persistence. RELOCATED here from settings-hub
 *      2026-06-23 so BOTH the Settings → Shortcuts tab AND the Email Hub's
 *      bottom shortcuts panel can read/write the SAME slice from ONE hook —
 *      email-hub can't import settings-hub (settings-hub already imports
 *      email-hub, so that edge would be a cycle), but both depend on
 *      tenant-shared. settings-hub now re-exports this.
 *
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌──────────────────────────┐
 *   │ localStorage  │ → │ initial render      │ → │ overrides + mutators       │
 *   │ (sync, coerced)│  │ then GET /settings/ │   │ each mutate → setState +   │
 *   └──────────────┘   │ ui/ hydrates        │   │ localStorage + PUT (slice) │
 *                      └─────────────────────┘   └──────────────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui: ShortcutOverrideMap, coerceOverrideMap, mergeOverride,
 *     clearOverride, trackSettingsEvent, SHORTCUT_OVERRIDES_* constants
 *   - ./quotechSettings: getUiPreferences, saveUiPreferences
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  SHORTCUT_OVERRIDES_CHANGED_EVENT,
  SHORTCUT_OVERRIDES_STORAGE_KEY,
  clearOverride,
  coerceOverrideMap,
  mergeOverride,
  trackSettingsEvent,
  type ShortcutOverrideMap,
} from '@quotech/ui'
import { getUiPreferences, saveUiPreferences } from './quotechSettings'

const STORAGE_KEY = SHORTCUT_OVERRIDES_STORAGE_KEY

const SAVE_ERROR =
  "Couldn't sync your shortcut changes. They're saved on this device and will sync next time."

function readLocal(): ShortcutOverrideMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return coerceOverrideMap(JSON.parse(raw))
  } catch {
    // localStorage unavailable / malformed JSON — start from defaults
  }
  return {}
}

function writeLocal(map: ShortcutOverrideMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // best-effort only
  }
  // Same-tab broadcast so the app-shell dispatcher reader
  // (useShortcutOverridesRuntime) picks up the change immediately — the native
  // `storage` event only fires in OTHER tabs. Guarded for SSR / no-window.
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(SHORTCUT_OVERRIDES_CHANGED_EVENT))
    }
  } catch {
    // CustomEvent/dispatch unavailable — runtime reader still hydrates on mount
  }
}

export interface UseShortcutOverridesResult {
  /** Current effective override map (id → { binding?, enabled? }). */
  overrides: ShortcutOverrideMap
  /** True until the first backend hydration resolves (success OR failure). */
  loading: boolean
  /** Non-null when the last load/save failed (UI stays usable from local). */
  error: string | null
  /** Remap an action to a new binding (null = explicitly unbound). */
  remap: (id: string, binding: string | null) => void
  /** Enable/disable an action without changing its binding. */
  setEnabled: (id: string, enabled: boolean) => void
  /** Reset one action back to its default (clears its override). */
  reset: (id: string) => void
  /** Reset every customization. */
  resetAll: () => void
}

/**
 * useShortcutOverrides — the customization layer's data source. Initialises
 * synchronously from localStorage (no flash), hydrates from the backend on
 * mount, and persists every mutation to local + backend (fire-and-forget; a
 * failed PUT surfaces `error` but never blocks the editor). Telemetry is fired
 * on load / remap / toggle / reset / reset-all.
 */
export function useShortcutOverrides(): UseShortcutOverridesResult {
  const [overrides, setOverrides] = useState<ShortcutOverrideMap>(readLocal)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Latest map without stale closures, and a guard so a late backend hydration
  // never clobbers a change the operator made while the GET was in flight.
  const overridesRef = useRef(overrides)
  const userTouchedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const prefs = await getUiPreferences()
        const remote = coerceOverrideMap(prefs?.shortcut_overrides)
        if (!cancelled && !userTouchedRef.current) {
          overridesRef.current = remote
          setOverrides(remote)
          writeLocal(remote)
          trackSettingsEvent('shortcuts.overrides_load', {
            count: Object.keys(remote).length,
          })
        }
      } catch {
        // offline or endpoint unavailable — keep the localStorage value
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const apply = useCallback((next: ShortcutOverrideMap) => {
    overridesRef.current = next
    userTouchedRef.current = true
    setOverrides(next)
    writeLocal(next)
    void saveUiPreferences({ shortcut_overrides: next })
      .then(() => setError(null))
      .catch(() => setError(SAVE_ERROR))
  }, [])

  const remap = useCallback(
    (id: string, binding: string | null) => {
      trackSettingsEvent('shortcuts.override_remap', { id })
      apply(mergeOverride(overridesRef.current, id, { binding }))
    },
    [apply],
  )

  const setEnabled = useCallback(
    (id: string, enabled: boolean) => {
      trackSettingsEvent('shortcuts.override_toggle', { id, enabled })
      apply(mergeOverride(overridesRef.current, id, { enabled }))
    },
    [apply],
  )

  const reset = useCallback(
    (id: string) => {
      trackSettingsEvent('shortcuts.override_reset', { id })
      apply(clearOverride(overridesRef.current, id))
    },
    [apply],
  )

  const resetAll = useCallback(() => {
    trackSettingsEvent('shortcuts.overrides_reset_all', {
      count: Object.keys(overridesRef.current).length,
    })
    apply({})
  }, [apply])

  return { overrides, loading, error, remap, setEnabled, reset, resetAll }
}
